import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Mail, Clock, CheckCircle2, XCircle, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";

interface SalesStats {
  total: number;
  pending: number;
  consumed: number;
  expired: number;
}

async function fetchSalesStats(): Promise<SalesStats> {
  const res = await fetch("/api/sales/stats", { credentials: "include" });
  if (!res.ok) throw new Error("Failed to fetch stats");
  return res.json();
}

function StatCard({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: typeof Mail;
  label: string;
  value: number;
  color: string;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 flex items-center gap-4 shadow-sm">
      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${color}`}>
        <Icon className="h-5 w-5 text-white" />
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900">{value}</p>
        <p className="text-sm text-slate-500">{label}</p>
      </div>
    </div>
  );
}

export default function SalesDashboard() {
  const { data, isLoading, isError } = useQuery<SalesStats>({
    queryKey: ["/api/sales/stats"],
    queryFn: fetchSalesStats,
  });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-slate-500">
        Failed to load stats. Please refresh.
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Overview of your company invitations</p>
        </div>
        <Link href="/sales/invitations" asChild>
          <Button className="rounded-2xl bg-primary hover:bg-primary/80 font-bold">
            <Plus className="h-4 w-4 mr-2" />
            Create Invitation
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={Mail} label="Total invites" value={data.total} color="bg-primary" />
        <StatCard icon={Clock} label="Pending" value={data.pending} color="bg-amber-400" />
        <StatCard icon={CheckCircle2} label="Joined" value={data.consumed} color="bg-emerald-500" />
        <StatCard icon={XCircle} label="Expired" value={data.expired} color="bg-red-400" />
      </div>
    </div>
  );
}
